import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  getAdminStatisticsHistory,
  getAdminStatisticsOverview,
  getAdminStatisticsTrends,
} from "#/modules/admin.statistics/api.statistics-admin.server";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";

const TREND_WEEKS = 26;

// Mirrors AdminStatHistorySoort on the backend: the stats whose history can be
// reconstructed (sometimes approximated) from existing timestamps.
export const STAT_HISTORY_SOORTEN = [
  "ScholenMetLeerlingen",
  "TotaalLeerlingen",
  "LessenGegeven",
  "ScoresIngevoerd",
  "WachtendOpActivatie",
  "ActieveScholenMetRecenteLes",
  "OpenTickets",
  "Abonnementen",
  "NieuweScholen",
  "ShowcaseScholen",
  "OpenstaandeInvites",
  "MediaanDagenTotEersteLes",
  "ActieveDocenten",
  "ScholenZonderLeerlingen",
] as const;

/** GET — all data for the admin dashboard: KPI tiles and trend series. */
export const getAdminDashboardData = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .handler(async () => {
    const [overviewResult, trendsResult] = await Promise.all([
      getAdminStatisticsOverview(),
      getAdminStatisticsTrends(TREND_WEEKS),
    ]);
    if (Result.isError(overviewResult)) throw overviewResult.error;
    if (Result.isError(trendsResult)) throw trendsResult.error;

    return {
      overview: overviewResult.value!,
      trends: trendsResult.value!,
    };
  });

/** GET — weekly history of one stat tile, reconstructed from timestamps. */
export const getAdminStatHistory = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator(
    z.object({
      stat: z.enum(STAT_HISTORY_SOORTEN),
      weeks: z.number().int().min(1).max(104).default(TREND_WEEKS),
    }),
  )
  .handler(async ({ data }) => {
    const result = await getAdminStatisticsHistory(data.stat, data.weeks);
    if (Result.isError(result)) throw result.error;
    return result.value!;
  });
